import React, { useEffect, useRef } from 'react';
import { Button, Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import styles from './EditVoucherThemeModal.module.scss';
import { toast } from 'react-toastify';
import { updateVoucherTheme } from '../../api/voucherTheme';
import { getVoucherThemeCategories } from '../../api/voucherTheme';
import useComponentState from '../../utils/useComponentState';

// Validation constants
const EDIT_VOUCHER_THEME_FORM_FIELDS = [
  {
    name: 'name',
    label: 'Theme Name',
    type: 'text',
    placeholder: 'Enter theme name',
    required: true,
    validationMessage: 'Please provide a theme name.',
    maxLength: 50,
  },
  {
    name: 'imageFront',
    label: 'Front Image URL',
    type: 'text',
    placeholder: 'https://example.com/front_image.png',
    required: true,
    validationMessage: 'Front image URL is required',
    maxLength: 200,
  },
  {
    name: 'imageBack',
    label: 'Back Image URL',
    type: 'text',
    placeholder: 'https://example.com/back_image.png',
    required: true,
    validationMessage: 'Back image URL is required',
    maxLength: 200,
  },
  {
    name: 'mobile',
    label: 'Mobile Image URL',
    type: 'text',
    placeholder: 'https://example.com/mobile_image.png',
    required: true,
    validationMessage: 'Mobile image URL is required',
    maxLength: 200,
  },
  {
    name: 'thumbnail',
    label: 'Thumbnail Image URL',
    type: 'text',
    placeholder: 'https://example.com/thumbnail_image.png',
    required: true,
    maxLength: 200,
  },
  {
    name: 'background',
    label: 'Background Image URL',
    type: 'text',
    placeholder: 'https://example.com/background_image.png',
    required: true,
    maxLength: 200,
  },
  {
    name: 'emailSubject',
    label: 'Email Subject',
    type: 'text',
    placeholder: 'Enter email subject',
    required: true,
    validationMessage: 'Email subject is required',
  },
  {
    name: 'emailContent',
    label: 'Email Content',
    type: 'textarea',
    placeholder: 'Enter email content',
    required: true,
    validationMessage: 'Email content is required',
    rows: 6,
  },
  {
    name: 'smsContent',
    label: 'SMS Content',
    type: 'textarea',
    placeholder: 'Enter SMS content',
    required: true,
    validationMessage: 'SMS content is required',
    rows: 6,
  },
];

// Validate URL field - must start with https://
const validateUrlField = (url) => {
  if (!url || url.trim() === '') {
    return null; // Empty URLs are handled by required field validation
  }
  if (!url.startsWith('https://')) {
    return 'URL must start with https://';
  }
  return null; // Valid URL
};

// Validate form data
const validateForm = (formData = {}) => {
  const errs = {};
  const imageUrlFields = [
    'imageFront',
    'imageBack',
    'mobile',
    'thumbnail',
    'background',
  ];

  // Validate required fields
  EDIT_VOUCHER_THEME_FORM_FIELDS.forEach((field) => {
    if (field.required) {
      const val = formData[field.name];
      const empty =
        val === null || typeof val === 'undefined' || String(val).trim() === '';
      if (empty) {
        errs[field.name] =
          field.validationMessage || `${field.label} is required`;
      }
    }
  });

  // Validate URL format for image URL fields
  imageUrlFields.forEach((fieldName) => {
    const urlValue = formData[fieldName];
    if (urlValue && urlValue.trim() !== '') {
      const urlError = validateUrlField(urlValue);
      if (urlError) {
        errs[fieldName] = urlError;
      }
    }
  });

  return errs;
};

const EditVoucherThemeModal = ({ show, onHide, theme, token, onSuccess }) => {
  const [state, setState] = useComponentState({
    ...theme,
    categories: [],
    fieldErrors: {}, // Track field-level errors
  });

  // Ref to store field references for scrolling
  const fieldRefs = useRef({});

  useEffect(() => {
    if (theme) {
      setState({
        ...theme,
        fieldErrors: {},
      });
    }
  }, [theme]);

  // Fetch categories on mount
  useEffect(() => {
    if (token) {
      fetchCategories();
    }
  }, [token]);

  const fetchCategories = async () => {
    try {
      const response = await getVoucherThemeCategories(token);
      if (response && response.voucherThemeCategories) {
        setState((prev) => ({
          ...prev,
          categories: response.voucherThemeCategories,
        }));
      }
    } catch (error) {
      toast.error('Failed to load voucher theme categories');
    }
  };

  const handleVoucherThemeUpdate = async () => {
    // Run JS validation
    const validationErrors = validateForm(state);
    if (Object.keys(validationErrors).length > 0) {
      setState((prevState) => ({
        ...prevState,
        fieldErrors: validationErrors,
      }));

      // Scroll to the first field with error (topmost)
      setTimeout(() => {
        const errorFields = Object.keys(validationErrors);
        const fieldOrder = EDIT_VOUCHER_THEME_FORM_FIELDS.map((f) => f.name);
        const firstErrorField = errorFields.sort(
          (a, b) => fieldOrder.indexOf(a) - fieldOrder.indexOf(b)
        )[0];

        if (firstErrorField && fieldRefs.current[firstErrorField]) {
          fieldRefs.current[firstErrorField].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          fieldRefs.current[firstErrorField].focus();
        }
      }, 0);

      return;
    }

    // Clear errors and proceed
    setState((prevState) => ({
      ...prevState,
      fieldErrors: {},
    }));

    const payload = {
      voucherThemeId: state.id,
      voucherThemeCategoryId: state.selectedCategoryId
        ? state.selectedCategoryId
        : state.voucherThemeCategory?.id,
      themeName: state.name,
      smsContent: state.smsContent,
      emailContent: state.emailContent,
      emailSubject: state.emailSubject,
      backgroundImageUrl: state.imageBack,
      thumbnailImageUrl: state.thumbnail,
      imageFront: state.imageFront,
      imageBack: state.imageBack,
      mobileImageUrl: state.mobile,
    };

    try {
      const response = await updateVoucherTheme(payload, token);
      if (response.message === 'VOUCHER_THEME_UPDATED_SUCCESSFULLY') {
        onSuccess();
        onHide();
        toast.success(`Voucher theme updated successfully`);
        setState({
          voucherThemeId: '',
          voucherThemeCategoryId: '',
          themeName: '',
          smsContent: '',
          emailContent: '',
          emailSubject: '',
          backgroundImageUrl: '',
          thumbnailImageUrl: '',
          imageFront: '',
          imageBack: '',
          mobileImageUrl: '',
          fieldErrors: {},
        });
      }
    } catch (error) {
      console.error('Error updating voucher theme:', error);
      toast.error('Failed to update voucher theme');
    }
  };

  // Reusable input field with validation
  const renderInput = ({
    id,
    label,
    type = 'text',
    placeholder,
    value,
    onChange,
    rows,
    required = false,
    maxLength,
  }) => {
    const isInvalid = !!state.fieldErrors[id];
    const currentLength = value ? value.length : 0;
    const isNearLimit =
      maxLength &&
      currentLength >= Math.ceil(maxLength * 0.8) &&
      currentLength < maxLength;
    const isAtLimit = maxLength && currentLength === maxLength;

    const controlClasses = `form-control ${isAtLimit ? 'at-limit' : ''} ${
      isNearLimit && !isAtLimit ? 'near-limit' : ''
    }`;
    const counterClasses = `small ${
      isAtLimit ? 'text-danger' : isNearLimit ? 'text-warning' : ''
    }`;

    return (
      <Form.Group
        className='mb-3'
        controlId={id}
        ref={(el) => {
          if (el) fieldRefs.current[id] = el;
        }}
      >
        <Form.Label>
          {label} {required && <span className='text-danger'>*</span>}
        </Form.Label>
        {rows ? (
          <Form.Control
            as='textarea'
            rows={rows}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            isInvalid={isInvalid || isAtLimit}
            maxLength={maxLength}
            className={controlClasses}
          />
        ) : (
          <Form.Control
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            isInvalid={isInvalid || isAtLimit}
            maxLength={maxLength}
            className={controlClasses}
          />
        )}

        {/* Error (left) + Counter (right) */}
        <div className='d-flex justify-content-between align-items-center mt-1'>
          {/* Validation error */}
          {isInvalid && (
            <div className='invalid-feedback d-block'>
              {state.fieldErrors[id]}
            </div>
          )}

          {/* Character counter */}
          {maxLength && (
            <small className={`${counterClasses} ms-auto`}>
              {currentLength}/{maxLength}
            </small>
          )}
        </div>
      </Form.Group>
    );
  };

  const handleInputChange = (fieldName) => (e) => {
    const { value } = e.target;
    setState({
      [fieldName]: value,
      fieldErrors: {}, // Clear field errors when user starts typing
    });
  };

  return (
    <Modal
      show={show}
      className={styles.editMerchantModal}
      onHide={onHide}
      size='lg'
      centered
      backdrop={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Voucher Theme</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {renderInput({
            id: 'name',
            label: 'Theme Name',
            placeholder: 'Enter theme name',
            value: state.name,
            onChange: handleInputChange('name'),
            required: true,
            maxLength: 50,
          })}

          <Form.Group className='mb-3' controlId='category'>
            <Form.Label>Voucher Theme Category</Form.Label>
            <Form.Control
              as='select'
              value={state.voucherThemeCategory?.id ?? ''}
              onChange={(e) => {
                const value = Number(e.target.value);
                const selectedCategory = state.categories.find(
                  (c) => c.id === value
                );
                setState((prev) => ({
                  ...prev,
                  voucherThemeCategory: {
                    id: value,
                    name: selectedCategory?.name,
                    defaultThemeId: selectedCategory?.defaultThemeId,
                  },
                  selectedCategoryId: value,
                }));
              }}
            >
              <option value='' disabled>
                Select a category
              </option>
              {state.categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {renderInput({
            id: 'imageFront',
            label: 'Front Image URL',
            placeholder: 'https://example.com/front_image.png',
            value: state.imageFront,
            onChange: handleInputChange('imageFront'),
            required: true,
            maxLength: 200,
          })}

          {renderInput({
            id: 'imageBack',
            label: 'Back Image URL',
            placeholder: 'https://example.com/back_image.png',
            value: state.imageBack,
            onChange: handleInputChange('imageBack'),
            required: true,
            maxLength: 200,
          })}

          {renderInput({
            id: 'mobile',
            label: 'Mobile Image URL',
            placeholder: 'https://example.com/mobile_image.png',
            value: state.mobile,
            onChange: handleInputChange('mobile'),
            required: true,
            maxLength: 200,
          })}

          {renderInput({
            id: 'background',
            label: 'Background Image URL',
            placeholder: 'https://example.com/background_image.png',
            value: state.background,
            onChange: handleInputChange('background'),
            required: true,
            maxLength: 200,
          })}

          {renderInput({
            id: 'thumbnail',
            label: 'Thumbnail Image URL',
            placeholder: 'https://example.com/thumbnail_image.png',
            value: state.thumbnail,
            onChange: handleInputChange('thumbnail'),
            required: true,
            maxLength: 200,
          })}

          {renderInput({
            id: 'emailSubject',
            label: 'Email Subject',
            placeholder: 'Enter email subject',
            value: state.emailSubject,
            onChange: handleInputChange('emailSubject'),
            required: true,
          })}

          {renderInput({
            id: 'emailContent',
            label: 'Email Content',
            placeholder: 'Enter email content',
            value: state.emailContent,
            onChange: handleInputChange('emailContent'),
            required: true,
            rows: 6,
          })}

          {renderInput({
            id: 'smsContent',
            label: 'SMS Content',
            placeholder: 'Enter SMS content',
            value: state.smsContent,
            onChange: handleInputChange('smsContent'),
            required: true,
            rows: 6,
          })}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Close
        </Button>
        <Button variant='primary' onClick={handleVoucherThemeUpdate}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditVoucherThemeModal;
